/**
 * ApprovalPopout — inline approval card rendered under the spine
 * row whose section needs the writer's call.
 *
 * Surfaces:
 *   - Tier badge (info / block / high-stakes) — drives border colour
 *   - Question prose — typically the most-severe issue's description
 *   - Context excerpt — the source span the issue points at
 *   - Two actions:
 *       Open Review  — focuses the section in the Inspector + Review tab
 *       Mark resolved — accepts the writer's call, run resumes
 *
 * Phase 5 deliberately ships two actions, not the design's three
 * (Approve / Edit / Reject). "Reject" needs a regenerate workflow
 * we don't have wired yet — promising it in the popout would be
 * dark UX. Two honest options is better than three with one stub.
 *
 * SRP: pure presentation. Receives the PendingApproval + handlers
 * via props. Doesn't dispatch, doesn't know about the reducer.
 */

import { makeStyles, mergeClasses } from "@fluentui/react-components";
import type React from "react";
import type { PendingApproval } from "../state";
import { brandColors, fonts, inkColors, statusColors, surfaceColors } from "../tokens";

interface ApprovalPopoutProps {
  approval: PendingApproval;
  /** Writer accepted the call. Shell dispatches APPROVAL_RESOLVED. */
  onAccept: () => void;
  /** Writer wants to inspect findings. Shell focuses the section
   *  + opens the Inspector to the Review tab. APPROVAL_RESOLVED
   *  fires too — the run is unblocked and the writer can act
   *  ambient-style from the inspector. */
  onOpenReview: () => void;
}

// ---------------------------------------------------------------------------
// Tier styling
// ---------------------------------------------------------------------------

interface TierStyle {
  borderColor: string;
  bg: string;
  badgeBg: string;
  badgeColor: string;
  label: string;
}

const TIER_STYLES: Record<PendingApproval["tier"], TierStyle> = {
  info: {
    borderColor: brandColors.mid,
    bg: brandColors.light,
    badgeBg: brandColors.primary,
    badgeColor: "#fff",
    label: "FYI",
  },
  block: {
    borderColor: statusColors.warn,
    bg: "#FFFBEB",
    badgeBg: statusColors.warn,
    badgeColor: "#7C2D12",
    label: "NEEDS REVIEW",
  },
  "high-stakes": {
    borderColor: statusColors.danger,
    bg: "#FEF2F2",
    badgeBg: statusColors.danger,
    badgeColor: "#fff",
    label: "BLOCKER",
  },
};

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles({
  root: {
    margin: "4px 12px 8px 80px", // aligns left edge to the spine label column
    borderRadius: "4px",
    padding: "10px 12px",
    boxShadow: "0 6px 20px -8px rgba(30, 20, 60, 0.20)",
    fontFamily: fonts.body,
  },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginBottom: "8px",
  },
  tierBadge: {
    fontSize: "8.5px",
    fontWeight: 800,
    letterSpacing: "0.6px",
    padding: "2px 6px",
    borderRadius: "2px",
    fontFamily: fonts.mono,
  },
  sectionRef: {
    fontSize: "11px",
    fontFamily: fonts.mono,
    color: inkColors.ink3,
  },
  question: {
    fontSize: "11.5px",
    color: inkColors.ink2,
    lineHeight: 1.45,
    marginBottom: "8px",
  },
  excerpt: {
    fontSize: "11px",
    color: inkColors.ink3,
    background: "rgba(255,255,255,0.6)",
    border: `1px solid ${surfaceColors.line}`,
    borderLeft: `2px solid ${surfaceColors.lineStrong}`,
    padding: "6px 8px",
    borderRadius: "2px",
    marginBottom: "10px",
    fontStyle: "italic",
  },
  actionRow: {
    display: "flex",
    gap: "6px",
    flexWrap: "wrap",
  },
  primaryBtn: {
    fontSize: "11px",
    fontWeight: 700,
    color: "#fff",
    background: brandColors.primary,
    border: "none",
    padding: "6px 12px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
  },
  secondaryBtn: {
    fontSize: "11px",
    fontWeight: 600,
    color: inkColors.ink2,
    background: "#fff",
    border: `1px solid ${surfaceColors.lineStrong}`,
    padding: "6px 10px",
    borderRadius: "3px",
    cursor: "pointer",
    fontFamily: fonts.body,
  },
});

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const ApprovalPopout: React.FC<ApprovalPopoutProps> = ({ approval, onAccept, onOpenReview }) => {
  const styles = useStyles();
  const tier = TIER_STYLES[approval.tier];

  return (
    <section
      className={styles.root}
      style={{
        background: tier.bg,
        border: `1px solid ${tier.borderColor}`,
        borderLeft: `3px solid ${tier.borderColor}`,
      }}
      aria-label={`Approval needed for section ${approval.sectionNumber}`}
    >
      <div className={styles.header}>
        <span className={styles.tierBadge} style={{ background: tier.badgeBg, color: tier.badgeColor }}>
          {tier.label}
        </span>
        <span className={styles.sectionRef}>§{approval.sectionNumber}</span>
      </div>
      <div className={styles.question}>{approval.question}</div>
      {approval.contextExcerpt && <div className={styles.excerpt}>&ldquo;{approval.contextExcerpt}&rdquo;</div>}
      <div className={styles.actionRow}>
        <button type="button" className={styles.primaryBtn} onClick={onOpenReview}>
          Open Review
        </button>
        <button type="button" className={mergeClasses(styles.secondaryBtn)} onClick={onAccept}>
          Mark resolved
        </button>
      </div>
    </section>
  );
};

export default ApprovalPopout;
